import React, { useState, useEffect, useRef } from "react";
import {
  Button,
  message,
  Popconfirm,
  Space,
  Table,
  Tag,
  theme,
  Tooltip,
  Typography,
} from "antd";
import {
  deleteIpServicesApi,
  getIpServicesListApi,
} from "../../../../services/receptionist.services";
import { DeleteOutlined, InfoCircleOutlined } from "@ant-design/icons";
import EditIcon from "../../../ui/EditIcon";
import { capitalizeFirstLetter, formatToINR } from "../../../../helpers/utility";
import useQuery from "../../../../hooks/useQuery";
import dayjs from "dayjs";
import {
  parsePageParam,
  updateUrlSearchParams,
} from "../../../../helpers/urlFilters";
import { useNavigate } from "react-router-dom";
import PractitionerModal from "./PractitionerModal";
import DeleteRequestModal from "./DeleteRequestModal";
import { IS_HOD, Role } from "../../../../constants/defaultKeys";
import { getCookie } from "../../../../helpers/localStorage";
import CommonPopOver from "../CommonPopOver";
import { UiDataTable } from "../../../ui";
import useIsMobile from "../../../../hooks/useIsMobile";
import usePageFillTableScroll from "../../../../hooks/usePageFillTableScroll";
import { getTablePagination } from "../../../../utils/tablePagination";

const { useToken } = theme;

const AssignedServicesTable = ({
  patientData,
  searchQuery = "",
  statusFilter = null,
  dateFilter = null,
  onCountChange,
}) => {
  const token = useToken;
  const role = getCookie(Role);
  const is_hod = getCookie(IS_HOD);
  const navigate = useNavigate();
  const params = useQuery();
  const page = parsePageParam(params, 1);
  const isMobile = useIsMobile();
  const tableWrapRef = useRef(null);
  const tableScroll = usePageFillTableScroll(tableWrapRef, isMobile, "services");
  const [services, setServices] = useState([]);
  const [refresh, setRefresh] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editData, setEditData] = useState(null);
  const [open, setOpen] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const admission = patientData?.admission?.is_active;

  const refreshServices = () => {
    setRefresh((prev) => !prev);
  };

  useEffect(() => {
    let param = {
      page: searchQuery ? 1 : page,
      limit: 100,
    };
    if (searchQuery) {
      param.search = searchQuery;
    }
    if (statusFilter) {
      param.status = statusFilter;
    }
    if (dateFilter) {
      param.date = dayjs(dateFilter).format("YYYY-MM-DD");
    }
    const queryString = new URLSearchParams(param);
    setLoading(true);
    getIpServicesListApi(`${params?.patient}`, queryString)
      .then((res) => {
        setServices(res?.data);
        setLoading(false);
      })
      .catch((err) => {
        console.log(err);
        setLoading(false);
      });
  }, [refresh, searchQuery, statusFilter, dateFilter, page, params?.patient]);

  const tableData = Array.isArray(services) ? services : services?.data ?? [];
  const assignedCount = services?.total_count ?? tableData.length ?? 0;

  useEffect(() => {
    onCountChange?.(assignedCount);
  }, [assignedCount, onCountChange]);

  const onDelete = (patient_service_id) => {
    deleteIpServicesApi({ service_ids: patient_service_id })
      .then((res) => {
        message.success(res?.data?.message);
        refreshServices();
        setSelectedRowKeys([]);
      })
      .catch((err) => {
        message.error(err?.response?.data?.message);
      });
  };

  const onSelectChange = (newSelectedRowKeys) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };
  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
    ...(isMobile
      ? {
          fixed: "left",
          columnWidth: 44,
        }
      : {}),
    getCheckboxProps: (record) => ({
      disabled: record.status !== "Ordered",
    }),
  };

  const bulkActions =
    selectedRowKeys?.length > 0 && admission ? (
      <Space size={4} className="services-bulk-actions">
        <Popconfirm
          title="Sure to delete?"
          onConfirm={() => {
            onDelete(selectedRowKeys);
          }}
        >
          <Button
            type="text"
            danger
            className="services-bulk-action-btn"
            aria-label="Delete"
            icon={<DeleteOutlined />}
          />
        </Popconfirm>
        <Button
          type="text"
          className="services-bulk-action-btn"
          aria-label="Edit"
          icon={<EditIcon size={16} />}
          onClick={() => {
            setOpen(true);
          }}
        />
      </Space>
    ) : null;

  const columns = [
    {
      title: "Service Name",
      dataIndex: "name",
      key: "name",
      width: isMobile ? 120 : 220,
      fixed: isMobile ? "left" : undefined,
      ellipsis: true,
      render: (value) => {
        const name = value || "—";
        if (!isMobile) {
          return (
            <Typography.Text
              style={{ display: "inline-block", maxWidth: "100%" }}
              ellipsis={{ tooltip: name }}
            >
              {name}
            </Typography.Text>
          );
        }
        return (
          <Tooltip title={name} trigger={["click"]} placement="topLeft">
            <Typography.Text
              style={{ display: "inline-block", maxWidth: "100%" }}
              ellipsis
            >
              {name}
            </Typography.Text>
          </Tooltip>
        );
      },
    },

    {
      title: "Price",
      dataIndex: "price",
      key: "price",
      width: 120,
      render: (value) => formatToINR(value),
    },
    {
      title: "Date",
      dataIndex: "from_date",
      key: "from_date",
      width: 140,
      render: (value) => {
        return dayjs(value).format("DD-MM-YYYY");
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 160,
      render: (value, record) => {
        return (
          <Space>
            <Tag
              color={
                value === "Delete_Requested"
                  ? "orange"
                  : value === "Ordered"
                  ? "blue"
                  : value === "Cancelled"
                  ? "red"
                  : "green"
              }
            >
              {value === "Delete_Requested"
                ? "Delete Requested"
                : capitalizeFirstLetter(value)}
            </Tag>
            {record?.service_delete_request_reason && (
              <CommonPopOver record={record} />
            )}
          </Space>
        );
      },
    },

    {
      title: "Updated By",
      dataIndex: "ordered_by_name",
      key: "ordered_by_name",
      ellipsis: true,
      // render: (value, record) => {
      //   return record?.performed_by ? capitalizeFirstLetter(value) : "-";
      // },
    },
    {
      title: "Performed By",
      dataIndex: "performed_by_name",
      key: "performed_by_name",
      ellipsis: true,
      render: (value) => {
        return capitalizeFirstLetter(value);
      },
    },
    ...(admission
      ? [
          {
            title: "Action",
            width: 100,
            align: "center",
            render: (_, record) => {
              return record?.status === "Done" &&
                !record?.service_delete_rejected ? (
                <Popconfirm
                  title="Sure to raise delete request?"
                  onConfirm={() => {
                    setEditData(record?.id);
                    setOpenDeleteModal(true);
                  }}
                >
                  <Button
                    type="text"
                    danger
                    className="services-row-action-btn"
                    aria-label="Delete"
                    icon={<DeleteOutlined />}
                  />
                </Popconfirm>
              ) : (
                ""
              );
            },
          },
        ]
      : []),
  ];

  return (
    <>
      {bulkActions ? (
        <div className="services-bulk-actions-bar">
          {bulkActions}
        </div>
      ) : null}
      <UiDataTable showSearch={false} showHeader={false} shellClassName="ui-table-fill-shell">
        <div ref={tableWrapRef}>
          <Table
            className="no-vertical-borders"
            tableLayout="fixed"
            rowKey="id"
            rowSelection={
              isMobile
                ? rowSelection
                : role === "Nurse"
                  ? is_hod === "true"
                    ? rowSelection
                    : null
                  : rowSelection
            }
            dataSource={tableData}
            columns={columns}
            pagination={getTablePagination({
              current: page,
              pageSize: 100,
              total: assignedCount,
              onChange: (p) => updateUrlSearchParams(navigate, { page: p }),
            })}
            loading={loading}
            scroll={isMobile ? { x: 980 } : tableScroll}
            bordered={false}
          />
        </div>
      </UiDataTable>
      {open && (
        <PractitionerModal
          open={open}
          setOpen={setOpen}
          editData={editData}
          refresh={refreshServices}
          selectedRowKeys={selectedRowKeys}
          setSelectedRowKeys={setSelectedRowKeys}
        />
      )}
      {openDeleteModal && (
        <DeleteRequestModal
          open={openDeleteModal}
          setOpen={setOpenDeleteModal}
          editData={editData}
          refresh={refreshServices}
          selectedRowKeys={selectedRowKeys}
          setSelectedRowKeys={setSelectedRowKeys}
        />
      )}
    </>
  );
};

export default AssignedServicesTable;
